import Label from '@/components/UI/Label';
import StudyStatusIndicator, { type StudyStatus } from '@/components/UI/StudyStatusIndicator/StudyStatusIndicator';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { type FC, type JSX } from 'react';
import { type LabelProps } from '../Label/Label';
import cn from 'classnames';

interface HeaderProps {
  mainHeaderText?: string;
  subHeaderText?: string;
  asideComponent?: () => JSX.Element;
  studyStatus?: StudyStatus;
  studyStatusDate?: string;
  studyStatusUser?: string;
  label?: LabelProps;
  testId?: string;
  className?: string;
}

const Header: FC<HeaderProps> = ({
  mainHeaderText,
  subHeaderText,
  asideComponent,
  studyStatus,
  studyStatusDate,
  studyStatusUser,
  label,
  className,
  testId = 'header-container',
}) => {
  // `Label` renders a div and `h1` only permits phrasing content, so the label is laid out as a
  // sibling of the heading. This also keeps the heading's text exactly `mainHeaderText`.
  const mainHeading = (
    <h1 className={cn('f3 normal', { pb2: _isNil(label) })} data-test-element="head">
      {mainHeaderText}
    </h1>
  );

  return (
    <div
      className={cn('pt3 flex justify-between flex-wrap', className)}
      data-test-component="Header"
      data-test-element="container"
      data-testid={testId}
    >
      <div>
        {_isNotBlank(subHeaderText) && (
          <h2 className="f5 dark-gray lh-title basier-reg normal pb1" data-test-element="sub-head">
            {subHeaderText}
          </h2>
        )}
        {_notNil(label) ? (
          <div className="flex items-center pb2">
            {mainHeading}
            <Label
              text={label.text}
              tooltip={label.tooltip}
              type={label.type}
              icon={label.icon}
              className="ml3 br4 ph2"
            />
          </div>
        ) : (
          mainHeading
        )}
      </div>
      {_notNil(studyStatus) ? (
        <div className="flex items-center g3">
          <StudyStatusIndicator status={studyStatus} date={studyStatusDate} user={studyStatusUser} />
          {asideComponent?.()}
        </div>
      ) : (
        asideComponent && <div>{asideComponent()}</div>
      )}
    </div>
  );
};

export default Header;
